<template>
  <div v-if="show" class="modal-overlay" @click.self="close">
    <div class="modal-content modal-xl">
      <div class="modal-header">
        <h3>Bill Details - {{ room?.roomNumber }}</h3>
        <button @click="close" class="close-btn">&times;</button>
      </div>

      <div class="modal-body">
        <div v-if="loading" class="loading-state">
          <div class="spinner"></div>
          <p>Loading bill details...</p>
        </div>

        <div v-else-if="error" class="error-message">
          <p>{{ error }}</p>
          <button @click="loadBillDetails" class="btn btn-primary btn-sm">Retry</button>
        </div>

        <div v-else-if="!billDetails" class="empty-state">
          <font-awesome-icon icon="fa-solid fa-file-invoice" class="empty-icon" />
          <p>No bill details found for this booking</p>
        </div>

        <div v-else class="bill-container">
          <!-- Summary Section -->
          <div class="bill-summary-card">
            <div class="summary-header">
              <div class="guest-info">
                <h4>{{ billDetails.guestName }}</h4>
                <p v-if="billDetails.guestEmail">{{ billDetails.guestEmail }}</p>
                <p v-if="billDetails.guestPhone">{{ billDetails.guestPhone }}</p>
                <p class="dates" v-if="billDetails.checkInDate">
                  In: {{ formatDate(billDetails.checkInDate) }} | Out: {{ formatDate(billDetails.checkOutDate) }}
                </p>
              </div>
              <div class="status-info">
                <span :class="`status-badge status-${(billDetails.billStatus || 'PENDING').toLowerCase()}`">
                  {{ formatStatus(billDetails.billStatus) }}
                </span>
              </div>
            </div>

            <div class="summary-grid">
              <div class="summary-item">
                <span class="label">Room Charges</span>
                <span class="value">{{ $utils.formatMoney(billDetails.roomCharges) }}</span>
              </div>
              <div class="summary-item">
                <span class="label">Service Charges</span>
                <span class="value">{{ $utils.formatMoney(billDetails.serviceCharges) }}</span>
              </div>
              <div class="summary-item">
                <span class="label">Minifridge Charges</span>
                <span class="value">{{ $utils.formatMoney(billDetails.minifridgeCharges) }}</span>
              </div>
              <div class="summary-item">
                <span class="label">Tax</span>
                <span class="value">{{ $utils.formatMoney(billDetails.taxAmount) }}</span>
              </div>
              <div class="summary-item total">
                <span class="label">Total Amount</span>
                <span class="value">{{ $utils.formatMoney(billDetails.totalAmount) }}</span>
              </div>
              <div class="summary-item paid">
                <span class="label">Total Paid</span>
                <span class="value">{{ $utils.formatMoney(billDetails.totalPaid) }}</span>
              </div>
              <div class="summary-item balance">
                <span class="label">Balance Due</span>
                <span class="value">{{ $utils.formatMoney(billDetails.balanceDue) }}</span>
              </div>
            </div>
          </div>

          <!-- Payments Section -->
          <div class="payments-section">
            <h4>Payment History</h4>
            <div v-if="billDetails.payments && billDetails.payments.length > 0" class="table-responsive">
              <table class="table table-hover">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Reference</th>
                    <th>Method</th>
                    <th class="text-end">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="payment in billDetails.payments" :key="payment.id">
                    <td>{{ formatDate(payment.createdAt) }}</td>
                    <td>{{ payment.transactionReference }}</td>
                    <td>{{ payment.paymentMethod }}</td>
                    <td class="text-end">{{ $utils.formatMoney(payment.amount) }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-else class="no-payments">
              <p>No payments recorded yet.</p>
            </div>
          </div>
        </div>
      </div>

      <div class="modal-footer">
        <button @click="close" class="btn btn-secondary">Close</button>
<!--        <button @click="printBill" class="btn btn-primary">-->
<!--          <font-awesome-icon icon="fa-solid fa-print" />-->
<!--          Print Bill-->
<!--        </button>-->
        <button
          v-if="billDetails && billDetails.balanceDue > 0"
          @click="processPayment"
          class="btn btn-success"
        >
          <font-awesome-icon icon="fa-solid fa-dollar-sign" />
          Pay Balance
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import LodgingIntegration from '@/services/lodgingIntegration';

export default {
  name: 'RoomBillModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    room: {
      type: Object,
      default: null
    }
  },
  data() {
    return {
      billDetails: null,
      loading: false,
      error: null
    };
  },
  watch: {
    show: {
      immediate: true,
      handler(newVal) {
        if (newVal && this.room) {
          this.loadBillDetails();
        }
      }
    }
  },
  methods: {
    async loadBillDetails() {
      this.loading = true;
      this.error = null;

      try {
        // Use bookingId from the room object.
        // Assuming room.bookingId is available as per previous updates.
        // If not, fallback to room.id if that was the previous convention,
        // but explicit bookingId is safer.
        const bookingId = this.room.bookingId || this.room.id;
        this.billDetails = await LodgingIntegration.getBookingDetails(bookingId);
      } catch (error) {
        console.error('Failed to load bill details:', error);
        this.error = error.message || 'Failed to load bill details';
      } finally {
        this.loading = false;
      }
    },

    close() {
      this.$emit('close');
    },

    processPayment() {
      this.$emit('process-payment', this.room);
    },

    printBill() {
      window.print();
    },

    formatStatus(status) {
      if (!status) return 'Pending';
      return status.replace(/_/g, ' ').toLowerCase()
        .replace(/\b\w/g, l => l.toUpperCase());
    },

    formatDate(dateString) {
      if (!dateString) return 'N/A';
      const date = new Date(dateString);
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

.modal-content {
  background: var(--card-bg);
  border-radius: 16px;
  max-width: 900px;
  width: 95%;
  max-height: 85vh;
  overflow: hidden;
  box-shadow: var(--card-shadow-hover);
  border: var(--card-border);
  display: flex;
  flex-direction: column;
}

.modal-xl {
  max-width: 1100px;
}

.modal-header {
  padding: 20px;
  border-bottom: 1px solid var(--card-border);
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: linear-gradient(135deg, var(--accent-electric-blue), var(--accent-neon-purple));
  color: white;
}

.modal-header h3 {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 2rem;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  transition: var(--transition-speed);
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.modal-body {
  padding: 20px;
  overflow-y: auto;
  flex: 1;
}

.loading-state,
.error-message,
.empty-state {
  text-align: center;
  padding: 60px 20px;
  color: var(--text-secondary);
}

.spinner {
  border: 4px solid var(--card-border);
  border-top: 4px solid var(--accent-electric-blue);
  border-radius: 50%;
  width: 50px;
  height: 50px;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

.empty-icon {
  font-size: 4rem;
  color: var(--accent-electric-blue);
  opacity: 0.3;
  margin-bottom: 20px;
}

.bill-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.bill-summary-card {
  background: var(--bg-primary);
  border: 1px solid var(--card-border);
  border-radius: 12px;
  padding: 20px;
}

.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid var(--card-border);
}

.guest-info h4 {
  margin: 0 0 5px 0;
  color: var(--accent-electric-blue);
  font-size: 1.4rem;
}

.guest-info p {
  margin: 0;
  color: var(--text-secondary);
  font-size: 0.9rem;
}

.status-badge {
  padding: 6px 16px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
}

.status-paid {
  background: rgba(40, 167, 69, 0.2);
  color: #28a745;
}

.status-pending, .status-checked_in {
  background: rgba(255, 193, 7, 0.2);
  color: #ffc107;
}

.status-overdue {
  background: rgba(220, 53, 69, 0.2);
  color: #dc3545;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 15px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  padding: 10px;
  background: var(--bg-secondary);
  border-radius: 8px;
}

.summary-item .label {
  font-size: 0.85rem;
  color: var(--text-secondary);
  margin-bottom: 4px;
}

.summary-item .value {
  font-size: 1.1rem;
  font-weight: 600;
  color: var(--text-primary);
}

.summary-item.total .value {
  color: var(--accent-electric-blue);
  font-size: 1.2rem;
}

.summary-item.paid .value {
  color: var(--success);
}

.summary-item.balance .value {
  color: var(--danger);
}

.payments-section {
  margin-top: 10px;
}

.payments-section h4 {
  margin-bottom: 15px;
  color: var(--text-primary);
}

.table {
  width: 100%;
  border-collapse: collapse;
  background: var(--card-bg);
  border-radius: 8px;
  overflow: hidden;
}

.table th,
.table td {
  padding: 12px;
  border-bottom: 1px solid var(--card-border);
  text-align: left;
  color: var(--text-primary);
  background: var(--card-bg);
}

.table th {
  background: var(--secondary-button-bg);
  font-weight: 600;
  color: var(--text-primary);
  text-transform: uppercase;
  font-size: 0.85rem;
  letter-spacing: 0.5px;
}

.table tbody tr {
  background: var(--card-bg);
  transition: var(--transition-speed);

  &:hover {
    background: var(--bg-secondary) !important;
  }

  &:not(:last-child) td {
    border-bottom: 1px solid var(--card-border);
  }
}

.table-hover tbody tr:hover {
  background: var(--bg-secondary) !important;
}

.text-end {
  text-align: right;
}

.no-payments {
  text-align: center;
  padding: 20px;
  color: var(--text-secondary);
  background: var(--bg-secondary);
  border-radius: 8px;
}

.modal-footer {
  padding: 20px;
  border-top: 1px solid var(--card-border);
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: var(--bg-secondary);
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}
</style>
